import { useState, useEffect } from "react";
import { Box, Card, Grid, Typography, Button, Input, Tooltip, TableContainer, Modal, Table, TableBody, TableHead, Checkbox, TableCell, TableRow, IconButton, FormControl, TextField, CardContent, useTheme, Divider } from "@mui/material";


import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import 'dayjs/plugin/customParseFormat';

import * as XLSX from "xlsx";

import CallAlertBar from '../../../Layout/Alertbar';
import { useNavigate } from "react-router-dom";
import Header from "../../../Layout/Header";
import Searchicon from '@mui/icons-material/Search';
import { tokens } from "../../../theme/theme";
import { API_ENDPOINTS } from "../../../config/api/apiCall";
import { callApi } from "../../../utils/apiClient";
import ReportsHeader from "../../../components/ReportHeader";


const MaterialStockUpload = ({handleBackWinderMachineEntry}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [qcdata, setQcdata] = useState([]);
    const navigate = useNavigate();
    const [qcupload, setQcupload] = useState(false);
    const [qcDetailsView, setqcDetailsView] = useState(true);
    const [qcExistingdata, setQcExistingdata] = useState([]);

    const [filters, setFilters] = useState({
        material_code: '',
        material_name: '',      
    });

    const filteredData = qcdata.filter((row) => {
        // Check if row matches selected status
        if (selectedStatus === 'All' || row.status === selectedStatus) {
            // Check if all filter fields match
            return Object.keys(filters).every((key) => {
                if (filters[key] === '') return true; // No filter applied
                return String(row[key]).toLowerCase().includes(filters[key].toLowerCase());
            });
        }
        return false;
    });

    const filteredDataExisting = qcExistingdata.filter((row) => {
        // Check if row matches selected status
        if (selectedStatus === 'All' || row.status === selectedStatus) {
            // Check if all filter fields match
            return Object.keys(filters).every((key) => {
                if (filters[key] === '') return true; // No filter applied
                return String(row[key]).toLowerCase().includes(filters[key].toLowerCase());
            });
        }
        return false;
    });


    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);

                    // Specify the cellDates option to handle date formatting
                    const workbook = XLSX.read(data, { type: 'array', cellDates: true });

                    // Assuming 'StockData' is the first sheet in the Excel file
                    const sheetName = workbook.SheetNames[0];
                    const sheet = workbook.Sheets[sheetName];

                    // Convert sheet data to JSON
                    const jsonData = XLSX.utils.sheet_to_json(sheet);

                    // Define expected columns
                    const expectedColumns = [
                        'Material Code',
                        'Material Name',
                        'Opening Quantity',
                    ];

                    // Validate if all expected columns are present in the data
                    const areColumnsValid = expectedColumns.every(col =>
                        jsonData.length && col in jsonData[0]
                    );

                    if (!jsonData.length) 
                    {
                        alert("No Material Stock Details found!");
                        return;
                    }

                    if (!areColumnsValid) {
                        alert("Invalid file! Please select a valid file with the expected columns.");
                        return;
                    }

                    // Add incremental IDs to each row and validate each field
                    const dataWithIds = jsonData.map((row, index) => {
                        const hasEmptyField = expectedColumns.some(col =>
                            row[col] === undefined || row[col] === null || row[col] === ''
                        );

                        return {
                            id: index + 1,
                            ...row,
                            isValid: !hasEmptyField, // true if no empty fields, false otherwise
                        };
                    });

                    // Set the data to state
                    setQcdata(dataWithIds);

                } catch (error) {
                    alert("An error occurred while processing the file. Please select a valid file.");
                    console.error("File processing error:", error);
                }
            };

            reader.readAsArrayBuffer(file);
        } else {
            alert("No file selected. Please select a valid file.");
        }
    };

    const downloadExcelFile = () => {
        const excelFilePath = '/excel-templates/MaterialStockTemplate.xlsx';
        const link = document.createElement('a');
        link.href = excelFilePath;
        link.download = 'MaterialStockTemplate.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const callApiForSaveCustomerOrderMappingData = async (requestBody) => {
        try {
            const response = await fetch(API_ENDPOINTS.MATERIAL_STOCK_SaveMaterialStockDetails, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });
            if (!response.ok) {
                throw new Error('Unable to update Material Stock Details');
            }
            const data = await response.json();
            if (data === 'Material stock details successfully imported.') {
                msg('info', 'Info', data);
                setQcdata([]);
                getAllCustomerOrderMappingDetails();
            }
            else {
                msg('error', 'Error', data);
            }
        } catch (error) {
            msg('error', 'Error', 'Unable to update Material Stock data');
            throw error; // rethrow the error so that the calling function can handle it
        }
    };

    // Function to prepare data for the API
    const prepareDataForAPI = () => {
        return qcdata
            .filter(row => selectedRows.includes(row.id)) // Only include selected rows
            .map(row => ({
                material_code: row["Material Code"],
                material_name: row["Material Name"],
                opening_quantity: row["Opening Quantity"],
            }));
    };

    async function SaveCustomerOrderData() {
        const formattedData = prepareDataForAPI();

        if (formattedData.length === 0) {
            // If no valid rows, handle the case (e.g., show a message to the user)
            msg('warning', 'Warning', 'Altease select one valid material stock data to import..!');
            return;
        }

        const requestBody = {
            obj: formattedData,
            p_companyid: localStorage.getItem('companyid'),
            p_plantid: 1,
        };

        await callApiForSaveCustomerOrderMappingData(requestBody);
    }

    // State to track selected rows
    const [selectedRows, setSelectedRows] = useState([]);

    // Handle Select All Click
    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            // Select all valid rows
            const allValidRowIds = filteredData.filter(row => row.isValid).map(row => row.id);
            setSelectedRows(allValidRowIds);
        } else {
            // Deselect all rows
            setSelectedRows([]);
        }
    };

    // Handle Individual Row Selection
    const handleRowCheckboxClick = (event, id) => {
        const selectedIndex = selectedRows.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedRows, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedRows.slice(1));
        } else if (selectedIndex === selectedRows.length - 1) {
            newSelected = newSelected.concat(selectedRows.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedRows.slice(0, selectedIndex),
                selectedRows.slice(selectedIndex + 1)
            );
        }
        setSelectedRows(newSelected);
    };

    // Check if a row is selected
    const isSelected = (id) => selectedRows.indexOf(id) !== -1;

    const [deckleMatchingMsgOpen, setDeckleMatchingMsgOpen] = useState(false);
    const [deckleMatchingSeverityvalue, setDeckleMatchingSeverityvalue] = useState('info');
    const [deckleMatchingMessageValue, setDeckleMatchingMessageValue] = useState('Welcome to Paper Roll Booking! ');
    const [deckleMatchingAlertTitleValue, setDeckleMatchingAlertTitleValue] = useState('');

    const msg = (severityvalue, title, messageValue) => {
        setDeckleMatchingSeverityvalue(severityvalue);
        setDeckleMatchingMessageValue(messageValue);
        setDeckleMatchingAlertTitleValue(title);
        setDeckleMatchingMsgOpen(true);
    };

    const getAllCustomerOrderMappingDetails = () => {
        let parameters = {
            p_companyid: localStorage.getItem('companyid'),
            p_plantid:1
        };

        callApi(API_ENDPOINTS.MATERIAL_STOCK_GetMaterialStockDetails, 'POST', { 'Content-Type': 'application/json' }, parameters)
            .then(data => {

                const dataWithSerialNumbers = data.map((item, index) => ({
                    ...item,
                    id: index + 1, // Generate serial number starting from 1
                }));
                // Set the modified data
                setQcExistingdata(dataWithSerialNumbers);

            })
            .catch(error => {
                // // Update state with error message        
                // setReelWeightementSeverityvalue('error');
                // setReelWeightementMessageValue(error.message);
                // setReelWeightementAlertTitleValue('Error');
                // setReelWeightementMsgOpen(true);
            });

    };

    const handleViewQCUpload = () => {
        setQcupload(true);
        setqcDetailsView(false);
    }

    const handleViewQCDetailsView = () => {
        setQcupload(false);
        setqcDetailsView(true);
    }


    const handleClosePage = () => {
       handleBackWinderMachineEntry();
    }

    const handleClose = () => {
        setOpen(false);
    };

    useEffect(() => {
        if (qcExistingdata) {
            getAllCustomerOrderMappingDetails();
        }

    }, []);

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters({ ...filters, [name]: value });
    };

    const [open, setOpen] = useState(false);
    const handleOpen = () => {
        setOpen(true);
    };

    return (
        <>
            {qcDetailsView && (
                <Box >

                    <Card style={{ backgroundColor: 'transparent' }}>
                        <Box style={{}} flexDirection={'row'} p={'5px'}>
                            <ReportsHeader title={'Material Stock List'} subtitle="" callback={handleClosePage} />
                        </Box>
                    </Card>


                    <Box >
                        <Box display="flex" flexDirection={'column'}>
                            <Box m='0px' display="flex" justifyContent={'flex-start'}>
                                <Grid container spacing={1} justifyContent="flex-end" style={{ marginTop: '0px' }}>
                                    <Grid item>
                                        <Box display="flex" justifyContent={'end'}>
{/* 
                                            <IconButton style={{ borderRadius: '8px' }} onClick={handleViewQCUpload}>
                                                <DownloadIcon style={{ marginRight: '5px', color: '#1e88e5' }} />
                                                <Typography variant="button" textTransform={'none'} fontSize={13} > Upload Stock List</Typography>
                                            </IconButton> */}

                                             <Button size="small" variant="contained" onClick={handleViewQCUpload} color="secondary"  startIcon={<FileUploadRoundedIcon />} sx={{ marginLeft: 'auto',margin:'5px', textTransform: 'none' }}>Upload Stock List</Button>
                                            

                                        </Box>


                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>

                        <Box
                            gridColumn="span 9"
                            gridRow="span 2"
                            alignItems="center"
                            justifyContent="center"
                            padding="0px 10px 0 10px"
                            sx={{
                                "& .MuiTable-root": {
                                    border: "none",
                                    size: "small",
                                },
                                "& .MuiTableCell-root": {
                                    borderBottom: "none",
                                    border: "1px solid",
                                    borderColor: `${colors.primary[400]} !important`,
                                    padding: "2px",  // Further reduced padding
                                },
                                "& .MuiTableHead-root": {
                                    borderBottom: "none",
                                },
                                "& .MuiTableBody-root": {
                                    overflowY: "auto",
                                },
                                "& .MuiCheckbox-root": {
                                    color: `${colors.blueAccent[200]} !important`,
                                    size: "small",
                                },
                                "& .MuiTableFooter-root": {
                                    borderTop: "none",
                                    backgroundColor: colors.blueAccent[700],
                                    height: "10px",
                                },
                                "& .MuiButton-text": {
                                    color: `${colors.grey[100]} !important`,
                                },
                                // scroll bar
                                maxHeight: {
                                    xs: '72vh', // Extra small devices (phones)
                                    sm: '72vh', // Small devices (tablets)
                                    md: '74vh', // Medium devices (small laptops)
                                    lg: '74vh', // Large devices (desktops)
                                    xl: '80vh', // Extra large devices (large desktops)
                                },

                                overflowY: 'auto', // Add overflowY: 'auto' to enable vertical scrolling
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: colors.primary[400],
                                    borderRadius: '10px',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: colors.blueAccent[600],
                                    borderRadius: '10px',
                                    '&:hover': {
                                        background: colors.blueAccent[800],
                                    },
                                },
                                '&::-webkit-scrollbar-corner': {
                                    background: colors.primary[700],
                                }
                            }}
                        >
                            <TableContainer >
                                <Table stickyHeader size="small">
                                    <TableHead sx={{ height: 30, fontSize: '16px' }}>
                                        <TableRow>
                                            <TableCell></TableCell>
                                            <TableCell>MATERIAL CODE</TableCell>
                                            <TableCell>MATERIAL NAME</TableCell>
                                            <TableCell>OPENING QUANTITY</TableCell>
                                            <TableCell>UOM</TableCell>
                                            <TableCell>
                                                <IconButton onClick={handleOpen}>
                                                    <Searchicon style={{ fontSize: "16px" }} />
                                                </IconButton>
                                                <Modal
                                                    open={open}
                                                    onClose={handleClose}
                                                    aria-labelledby="modal-modal-title"
                                                    aria-describedby="modal-modal-description"
                                                >
                                                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, minWidth: 300 }}>
                                                        <TextField
                                                            label="Material Code"
                                                            name="material_code"
                                                            size="small"
                                                            value={filters.material_code}
                                                            onChange={handleFilterChange}
                                                            variant="outlined"
                                                            fullWidth
                                                            margin="normal"
                                                        />
                                                        <TextField
                                                            label="Material Name"
                                                            name="material_name"
                                                            size="small"
                                                            value={filters.material_name}
                                                            onChange={handleFilterChange}
                                                            variant="outlined"
                                                            fullWidth
                                                            margin="normal"
                                                        />

                                                        <Button variant="contained" style={{ marginTop: 10 }} onClick={handleClose} >
                                                            {/* onClick={handleClose} */}
                                                            Close
                                                        </Button>
                                                    </Box>
                                                </Modal>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody >
                                        {filteredDataExisting.map((row) => (
                                            <TableRow sx={{ height: 30 }} key={row.id}>
                                                <TableCell>{row.id}</TableCell>
                                                <TableCell>{row.material_code}</TableCell>
                                                <TableCell>{row.material_name}</TableCell>
                                                <TableCell>{row.opening_quantity}</TableCell>
                                                 <TableCell>{row.material_uom}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>

                    </Box>

                </Box>

            )}

            {qcupload && (
                <Box >

                    {/* <Card style={{backgroundColor:'transparent'}}> */}
                    <Box style={{}} flexDirection={'row'} p={'5px'}>
                        {/* <ReportsHeader title={'Order Mapping with New CO No'} subtitle="" callback={handleViewQCDetailsView} /> */}
                        <Header title={'Material Stock Details Upload'} subtitle="" callback={handleViewQCDetailsView} />

                        <Divider />
                    </Box>
                    {/* </Card> */}


                    <CallAlertBar
                        parentRollMsgOpen={deckleMatchingMsgOpen}
                        setParentRollMsgOpen={setDeckleMatchingMsgOpen}
                        parentRollSeverityvalue={deckleMatchingSeverityvalue}
                        setParentRollSeverityvalue={setDeckleMatchingSeverityvalue}
                        parentRollAlertTitleValue={deckleMatchingAlertTitleValue}
                        setParentRollAlertTitleValue={setDeckleMatchingAlertTitleValue}
                        parentRollMessageValue={deckleMatchingMessageValue}
                        setParentRollMessageValue={setDeckleMatchingMessageValue}
                    />

                    <CardContent >
                        <Box display="flex" flexDirection={'column'}>

                            <Box m='0px' display="flex" justifyContent="flex-start" alignItems="center">
                                <Box display="flex" mr={1} >
                                    <FormControl fullWidth>
                                        <Input
                                            id="file-input"
                                            type="file"
                                            accept=".xlsx, .xls"
                                            onChange={handleFileChange}
                                            sx={{ display: 'none' }}
                                        />
                                        <label htmlFor="file-input" style={{ cursor: "pointer" }}>
                                            <Tooltip title="Select Material Stock Excel File" component="span">
                                                <Box display="flex" alignItems="center">
                                                    <IconButton component="span"> {/* Set `component="span"` to make it work as part of the label */}
                                                        <FileUploadRoundedIcon style={{ color: "#1de9b6" }} />
                                                    </IconButton>
                                                    <Typography align="left" fontSize={13} style={{ cursor: "pointer" }}>
                                                        Select
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        </label>
                                    </FormControl>


                                    <FormControl fullWidth>
                                        <Tooltip title="Save Material stock details" onClick={SaveCustomerOrderData}>
                                            <Box display="flex" alignItems="center">
                                                <IconButton>
                                                    <SaveOutlinedIcon style={{ color: "#388e3c" }} />
                                                </IconButton>
                                                <Typography align="left" fontSize={13} style={{ cursor: "pointer" }}>
                                                    Save
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    </FormControl>

                                    <FormControl fullWidth>
                                        <Tooltip title="Material Stock Excel Template" onClick={downloadExcelFile}>
                                            <Box display="flex" alignItems="center">
                                                <IconButton>
                                                    <FileDownloadRoundedIcon style={{ color: "#c0ca33" }} />
                                                </IconButton>
                                                <Typography align="left" fontSize={13} style={{ cursor: "pointer" }}>Download</Typography>
                                            </Box>
                                        </Tooltip>
                                    </FormControl>
                                </Box>
                            </Box>
                        </Box>

                        <Box textAlign="right" >
                            <Typography variant="subtitle1" gutterBottom>
                                * Invalid or missing data rows are highlighted in <span style={{ color: 'red' }}>red color</span>.
                            </Typography>
                        </Box>

                        <Divider sx={{ marginTop: '5px', marginBottom: '5px' }} />

                        <Box
                            gridColumn="span 9"
                            gridRow="span 2"
                            alignItems="center"
                            justifyContent="center"

                            sx={{
                                "& .MuiTable-root": {
                                    border: "none",
                                    size: "small",
                                },
                                "& .MuiTableCell-root": {
                                    borderBottom: "none",
                                    border: "1px solid",
                                    borderColor: `${colors.primary[400]} !important`,
                                    padding: "2px",  // Further reduced padding
                                },
                                "& .MuiTableHead-root": {
                                    borderBottom: "none",
                                },
                                "& .MuiTableBody-root": {
                                    overflowY: "auto",
                                },
                                "& .MuiCheckbox-root": {
                                    color: `${colors.blueAccent[200]} !important`,
                                    size: "small",
                                },
                                "& .MuiTableFooter-root": {
                                    borderTop: "none",
                                    backgroundColor: colors.blueAccent[700],
                                    height: "10px",
                                },
                                "& .MuiButton-text": {
                                    color: `${colors.grey[100]} !important`,
                                },

                                maxHeight: {
                                    xs: '72vh', // Extra small devices (phones)
                                    sm: '72vh', // Small devices (tablets)
                                    md: '74vh', // Medium devices (small laptops)
                                    lg: '63vh', // Large devices (desktops)
                                    xl: '80vh', // Extra large devices (large desktops)
                                },
                                overflowX: 'auto',
                                maxWidth: '100%',
                                overflowY: 'auto', // Add overflowY: 'auto' to enable vertical scrolling
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: colors.primary[400],
                                    borderRadius: '10px',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: colors.blueAccent[600],
                                    borderRadius: '10px',
                                    '&:hover': {
                                        background: colors.blueAccent[800],
                                    },
                                },
                                '&::-webkit-scrollbar-corner': {
                                    background: colors.primary[700],
                                }
                            }}
                        >
                            <TableContainer>
                                <Table size="small">
                                    <TableHead sx={{ height: 10 }}>
                                        <TableRow>
                                            <TableCell>
                                                <Checkbox
                                                    size="small"
                                                    style={{ color: "secondary" }}
                                                    indeterminate={selectedRows.length > 0 && selectedRows.length < filteredData.filter(row => row.isValid).length}
                                                    checked={selectedRows.length === filteredData.filter(row => row.isValid).length}
                                                    onChange={handleSelectAllClick}
                                                />
                                            </TableCell>
                                            <TableCell>MATERIAL CODE</TableCell>
                                            <TableCell>MATERIAL NAME</TableCell>
                                            <TableCell>OPENING QUANTITY</TableCell>
                                            
                                            <TableCell>
                                                <Modal
                                                    // open={open}
                                                    // onClose={handleClose}
                                                    aria-labelledby="modal-modal-title"
                                                    aria-describedby="modal-modal-description"
                                                >
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: '50%',
                                                            transform: 'translate(-50%, -50%)',
                                                            bgcolor: 'background.paper',
                                                            boxShadow: 24,
                                                            p: 4,
                                                            minWidth: 300
                                                        }}
                                                    >

                                                        <Button variant="contained" style={{ marginTop: 10 }}>
                                                            {/* onClick={handleClose} */}
                                                            Apply Filter
                                                        </Button>
                                                    </Box>
                                                </Modal>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredData.map((row) => {
                                            const isRowSelected = isSelected(row.id);
                                            const rowStyle = row.isValid
                                                ? {}
                                                : {
                                                    backgroundColor: '#823c3c', // Light background for invalid rows

                                                };
                                            return (
                                                <TableRow key={row.id} style={rowStyle}>
                                                    <TableCell>
                                                        <Checkbox
                                                            size="small"
                                                            checked={isRowSelected}
                                                            onChange={(event) => handleRowCheckboxClick(event, row.id)}
                                                            disabled={!row.isValid} // Disable checkbox if row has empty fields
                                                        />
                                                    </TableCell>
                                                    <TableCell>{row["Material Code"]}</TableCell>
                                                    <TableCell>{row["Material Name"]}</TableCell>
                                                    <TableCell>{row["Opening Quantity"]}</TableCell>
                                                    
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </CardContent>
                </Box>
            )}
        </>
    )
};
export default MaterialStockUpload;